import { useStoreState } from 'hooks';
import { useState } from 'react';
import { useLogin } from 'store/auth';
import { DATA_VALIDATION_SERVICE_URL } from 'utils/config';
import { shouldRenewTokenForDV } from 'utils/data-validation';

export enum JobType {
  COLLECTION = 'collection',
  RULESET = 'ruleset',
  RULE = 'rule',
}

export type AttributesType = Record<string, string[]>;

export type ValidationJobOptions = {
  attributes?: AttributesType;
  entityId: string;
  onError: (err: any) => void;
  onSuccess: () => void;
  schemaCollectionRefId?: string | null;
  type: JobType;
};

/**
 * Hook that allows the user to run a data valdation job. Data validation can be run on a single entity at a time;
 * the entity can be either a collection, a ruleset or a rule.
 * @returns startValidationJob: Function that starts a data validation job.
 * @returns validationJobRunning: Boolean value that keeps track whatever a validation job is running or not.
 */
export const useStartValidationJob = (): {
  startValidationJob: (options: ValidationJobOptions) => Promise<void>;
  validationJobRunning: boolean;
} => {
  let token = useStoreState((state) => state.auth.token);
  const tenant = useStoreState((state) => state.auth.tenant);
  const cluster = useStoreState((state) => state.auth.cluster);
  const expiry = useStoreState((state) => state.auth.expiry);
  const login = useLogin();

  const [validationJobRunning, setValidationJobRunning] = useState(false);

  const startValidationJob = async ({
    attributes,
    entityId,
    onError,
    onSuccess,
    schemaCollectionRefId,
    type,
  }: ValidationJobOptions) => {
    if (!entityId.trim()) {
      return;
    }

    setValidationJobRunning(true);

    if (shouldRenewTokenForDV(expiry))
      token = (await login({ tenant, useOIDC: true, forceRefresh: true }))
        .token;

    const data = JSON.stringify({
      id: entityId,
      type,
      attributes,
      schemaCollectionRefId: schemaCollectionRefId || '',
    });

    await fetch(DATA_VALIDATION_SERVICE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        project: `${tenant}`,
        cluster: `${cluster}`,
      },
      body: data,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Something went wrong with the validation job: ${res.statusText}.`
          );
        }

        onSuccess();
      })
      .catch(onError)
      .finally(() => setValidationJobRunning(false));
  };

  return { startValidationJob, validationJobRunning };
};
